import React from 'react';

// Canonical status label/tone mapping shared by the Challenges page, Profile
// page active-challenges list, and anywhere else a challenge/match status
// pill is rendered. Keep labels short so they never wrap on mobile.
//
// Usage:
//   <StatusPill status={ch.status} matchState={ch.match_state} />
//
// `status` is the raw challenge status (pending/accepted/completed/canceled/
// expired/declined/rejected) or one of the canonical labels (score, review,
// active, done). `matchState` refines an `accepted` challenge:
//   - 'awaiting_score'      → Score
//   - 'awaiting_approval'   → Review
//   - anything else         → Active

const TONE = {
  pending:  'bg-[#c2a251]/20 text-[#c2a251]',   // gold — waiting on response
  active:   'bg-[#015e3f]/10 text-[#015e3f]',   // emerald — in play
  score:    'bg-[#015e3f]/10 text-[#015e3f]',   // emerald — ready for score
  review:   'bg-[#c2a251]/20 text-[#c2a251]',   // gold — awaiting approval
  done:     'bg-[#015e3f]/10 text-[#015e3f]',   // emerald — completed
  canceled: 'bg-red-50 text-red-500',
  expired:  'bg-[#6a766e]/10 text-[#6a766e]',
  declined: 'bg-red-50 text-red-500',
  rejected: 'bg-red-100 text-red-600',
};

const LABEL = {
  pending:  'Pending',
  active:   'Active',
  score:    'Score',
  review:   'Review',
  done:     'Done',
  canceled: 'Canceled',
  expired:  'Expired',
  declined: 'Declined',
  rejected: 'Rejected',
};

export function resolveStatusKey(status, matchState) {
  // Accept canonical keys directly
  if (LABEL[status]) return status;
  const s = (status || '').toLowerCase();
  if (s === 'accepted') {
    if (matchState === 'awaiting_approval') return 'review';
    if (matchState === 'awaiting_score')    return 'score';
    return 'active';
  }
  if (s === 'completed') return 'done';
  if (LABEL[s]) return s;
  return null; // unknown — caller falls back to raw text
}

export default function StatusPill({ status, matchState, testId, className = '' }) {
  const key = resolveStatusKey(status, matchState);
  const label = key ? LABEL[key] : (status || '');
  const tone = key ? TONE[key] : 'bg-[#6a766e]/10 text-[#6a766e]';
  return (
    <span
      data-testid={testId}
      className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${tone} ${className}`}
    >
      {label}
    </span>
  );
}
